export default {
  common: {
    brand: 'DumpDeck',
    export: 'Export',
    startAnalysis: 'Start analysis →',
    analyze: 'Analyze',
    backToLanding: 'Back to landing',
    dropAFile: 'Drop a file',
    parsing: 'Parsing',
    networkOk: 'Network: {count} request{s}',
    verifyF12: 'F12 → Network to verify',
    f12Verify: 'F12 verify',
  },
  landing: {
    tagline: 'JVM Diagnostics Studio — your logs never leave your browser',
    sub: 'Drop a jstack / jcmd Thread.print output file (or paste text), see deadlock detection, lock contention, same-stack grouping in 30 seconds.',
    dropHere: 'Drop thread dump / GC log here',
    orClick: 'or click to pick files · multi-file supported',
    fileName: 'file name',
    pasteToFile: 'Paste to file',
    pastePlaceholder: 'Or paste jstack / GC log text here (Cmd+V)',
    loadedFiles: 'Loaded files ({count})',
    features: {
      privacy: 'No data leaves your browser — verify via F12',
      perf: 'Background parsing, 100MB+ files won\'t freeze UI',
      free: 'No upload, no account — analyze for free',
    },
  },
  thread: {
    tabs: {
      overview: 'Overview',
      deadlock: 'Deadlock',
      contention: 'Lock Contention',
      groups: 'Stack Groups',
      search: 'Search',
    },
    summary: {
      threads: 'threads',
      daemon: 'daemon',
      deadlocks: 'deadlocks',
      antiPatterns: 'anti-patterns',
      noIssues: '✓ No critical issues detected',
      expandAll: 'Show all {count}',
      collapse: 'Collapse',
      deadlockCycle: '{count} deadlock cycle(s)',
    },
    deadlock: {
      none: '✓ No deadlocks detected.',
      cycle: 'Cycle {n} — {count} threads',
    },
    contention: {
      none: '✓ No lock contention detected.',
      address: 'Lock address',
      holder: 'Holder',
      waiters: 'Waiters',
      count: 'Count',
      more: '+{count} more',
    },
    groups: {
      none: 'No same-stack groups (every thread has unique stack hash, or all stacks empty).',
    },
    search: {
      allStates: 'All states',
      namePlaceholder: 'Thread name contains...',
      stackPlaceholder: 'Stack contains (class/method)...',
      lockPlaceholder: 'Lock address 0x...',
      clear: 'Clear',
      resultCount: '{filtered} / {total} threads',
      clickHint: 'Click a thread to see detail',
    },
    detail: {
      stack: 'Stack ({count} frames)',
      locked: 'Locked',
      waitingToLock: 'Waiting to lock',
      parkingFor: 'Parking to wait for',
    },
    antiPatterns: {
      none: '✓ No anti-patterns detected.',
    },
    empty: 'No thread dump loaded.',
    parseError: 'Parse error: {error}',
    parsing: 'Parsing thread dump',
  },
  gc: {
    tabs: {
      metrics: 'Metrics',
      timeline: 'Pause Timeline',
      heap: 'Heap Curve',
      causes: 'Cause Attribution',
      tuning: 'Tuning Rules',
    },
    summary: {
      events: 'events',
      young: 'Young',
      full: 'Full',
      throughput: 'throughput',
      avg: 'avg',
      p99: 'P99',
      max: 'max',
      freq: 'freq',
      healthy: 'GC healthy',
      verdictMax: 'Max pause {ms}ms too long',
      verdictThroughput: 'Throughput {pct}% low',
      verdictFullGc: '{count} Full GC(s)',
    },
    timeline: {
      title: 'Pause Timeline',
      hint: 'Each GC is a point, colored by cause, sized by pause duration. Hover for details. Bottom slider for zoom.',
    },
    heap: {
      title: 'Heap Memory Curve',
      hint: 'Heap usage before/after each GC + total capacity (dashed). Metaspace in purple (right Y axis).',
    },
    causes: {
      title: 'Trigger Cause Distribution',
      hint: 'Pie chart by cause. Click legend to toggle.',
      fullGcDetail: 'Abnormal Pause / Full GC Details',
    },
    tuning: {
      none: '✓ No tuning rules triggered. GC behavior is within healthy range.',
      recommendation: 'Recommendation',
      evidence: 'Evidence',
      evidenceCount: '{count} events',
    },
    empty: 'No GC log loaded.',
    parseError: 'Parse error: {error}',
    parsing: 'Parsing GC log',
  },
  multi: {
    title: 'Thread Compare · {count} files',
    introTitle: 'Comparison explanation',
    intro: 'Compare multiple thread dumps, join by thread name, find state changes and unchanged stacks across dumps. Useful for locating CPU burners (RUNNABLE in all dumps with same stack) and persistently blocked threads.',
    cpuBurner: '🔥 CPU burner = all dumps RUNNABLE with same stack hash',
    persistentBlocked: '⛔ Persistently blocked = all dumps BLOCKED with same stack hash',
    loadedFiles: 'Loaded files (click to view single-dump analysis)',
    dump: 'Dump {n}',
    needTwo: 'Need at least 2 thread dumps to compare. Currently have {count}.',
    dropMore: 'Drop more files',
    parsing: 'Parsing {count} dumps...',
    kpis: {
      dumps: 'dumps compared',
      cpuBurners: 'CPU burners',
      persistentlyBlocked: 'persistently blocked',
      newThreads: 'new threads',
      disappeared: 'disappeared',
    },
    matrix: {
      title: 'State transition matrix (per thread)',
      thread: 'Thread',
      cpu: 'CPU?',
      stuck: 'Stuck?',
    },
    cpuBurnerList: 'CPU burners (RUNNABLE in all dumps, same stack)',
    persistentlyBlockedList: 'Persistently blocked (BLOCKED in all dumps, same stack)',
  },
  batch: {
    title: 'Batch Analysis · {count} files',
    threadSection: 'Thread Dumps',
    gcSection: 'GC Logs',
    paywallTitle: 'Batch Analysis is a Pro feature',
    paywallBody:
      'Free users can analyze up to 2 files at a time (same-kind thread dumps use Multi-dump view). Upgrade to Pro for unlimited batch analysis (up to 20 files, any mix).',
    upgrade: 'Upgrade to Pro',
    activate: 'Activate Pro',
    needTwo: 'Need at least 2 files to batch-analyze. Currently have {count}.',
    parsing: 'Parsing {count} files...',
    clickHint: 'Click any row to drill into that file\'s full analysis.',
    maxWarn: 'More than {max} files loaded — only the first {max} will be analyzed.',
    cols: {
      file: 'File',
      threads: 'Threads',
      deadlocks: 'Deadlocks',
      antiPatterns: 'Anti-patterns',
      topHotspot: 'Top hotspot',
      events: 'Events',
      throughput: 'Throughput',
      maxPause: 'Max pause',
      fullGc: 'Full GC',
      tuningRules: 'Tuning rules',
    },
    batchBtn: 'Batch Analysis ({count} files) →',
  },
  session: {
    title: 'Recent sessions (Pro)',
    clear: 'Clear all',
    saved: 'Analysis saved to local session (Pro)',
  },
  tuningRules: {
    title: 'Custom Tuning Rules',
    paywallTitle: 'Custom Tuning Rules is a Pro feature',
    paywallBody:
      'Free users use the built-in 30 rules with default thresholds. Upgrade to Pro to customize each rule\'s threshold and severity, or disable rules you don\'t need.',
    upgrade: 'Upgrade to Pro',
    activate: 'Activate Pro',
    resetAll: 'Reset all',
    resetAllConfirm: 'Clear all custom thresholds? This cannot be undone.',
    dirtyConfirm: 'The current form has unsaved changes. Discard?',
    list: {
      title: 'Rules ({count})',
      modified: 'Modified',
      disabled: 'Disabled',
      thresholdSummary: '{value} (default {default})',
      noThreshold: 'No threshold',
    },
    form: {
      enabled: 'Enable this rule',
      threshold: 'Threshold',
      thresholdDefault: 'Default: {value}{unit}',
      thresholdLessThan: 'Triggers when value is BELOW threshold',
      severity: 'Severity',
      severityInfo: 'Info',
      severityWarn: 'Warn',
      severityCritical: 'Critical',
      save: 'Save',
      reset: 'Reset to default',
      cancel: 'Cancel',
    },
    nav: {
      settingsButton: 'Custom tuning rules',
    },
  },
  feedback: {
    title: 'Send Feedback',
    intro:
      'Found a bug? Have a feature request? Want to share how you use DumpDeck? Drop it here — I read every one.',
    privacyNote:
      'Your text and screenshots are sent to our server, which logs them as a private issue. No tracking, no third-party analytics.',
    message: 'Message',
    placeholder: 'Describe what happened, what you expected, or what you\'d like to see...',
    addImages: 'Add screenshots',
    orDrop: 'or paste from clipboard (Cmd+V) / drag & drop here',
    removeImage: 'Remove',
    submit: 'Submit feedback',
    submitting: 'Submitting...',
    success: 'Feedback submitted. Thank you!',
    openIssue: 'Open in GitHub →',
    nav: {
      feedbackButton: 'Send Feedback',
    },
    errors: {
      generic: 'Failed to submit feedback',
      network: 'Network error',
      tooManyImages: 'Too many images (max {max})',
      imageTooLarge: 'Image {name} too large (max {max})',
    },
  },
  upgrade: {
    title: 'Upgrade to Pro',
    button: 'Upgrade to Pro',
    modalHint: 'Sandbox test mode — use card 4242 4242 4242 4242, any future expiry, any CVC.',
    priceDisclaimer: 'Prices shown are for reference. Final price and currency are confirmed at Paddle checkout.',
  },
  footer: {
    tagline: 'Browser-local JVM diagnostics. Data never leaves your browser.',
    terms: 'Terms',
    refundPolicy: 'Refund policy',
    privacy: 'Privacy',
    support: 'Support',
    manageSubscription: 'Manage subscription',
    activate: 'Activate Pro',
    builtBy: 'built by ZH Studio ·',
  },
  refundPolicy: {
    title: 'Cancellation & Refund',
    cancelTitle: 'Cancel subscription',
    cancelBody: 'Cancel anytime via the customer portal — stop future renewals while keeping Pro access until the end of your current billing period.',
    cancelCta: 'Open customer portal',
    refundTitle: 'Request refund',
    refundBody: 'Refunds are handled via Paddle. Open the customer portal to request a refund, or contact us via the feedback page if you need help.',
    refundCta: 'Open customer portal',
    refundCtaFallback: 'Open feedback page',
    diffTitle: 'Cancel vs refund — what is the difference?',
    diffBody: 'Cancelling stops future renewals — you keep Pro access until the end of your current billing period. Refunding gets your money back and revokes Pro access immediately.',
    contactTitle: 'Need help?',
    contactBody: 'Open the feedback page to send us a message.',
    contactCta: 'Open feedback page',
  },
  terms: {
    title: 'Terms of Service',
    effective: 'Effective date: {date}',
    acceptance: {
      title: 'Acceptance of terms',
      body: 'By accessing or using DumpDeck (the "Service"), you agree to be bound by these Terms of Service. If you do not agree to these terms, please do not use the Service.',
    },
    serviceDescription: {
      title: 'Service description',
      body: 'DumpDeck is a browser-based JVM diagnostics tool that analyzes thread dumps and GC logs locally in your web browser. All file content you upload stays in your browser unless you explicitly submit feedback through the feedback form. The Service includes both Free and Pro tiers with different feature sets.',
    },
    userResponsibilities: {
      title: 'User responsibilities',
      '1': 'You will use the Service only for lawful purposes and in compliance with all applicable laws.',
      '2': 'You will not attempt to reverse-engineer, decompile, or extract the source code of the Service beyond what is necessary to use it.',
      '3': 'You will not share, resell, or transfer your Pro license to other users. Each license is tied to a single email and is non-transferable.',
      '4': 'You will not use automated systems (bots, scrapers) to abuse the Service, including rate-limit circumvention or feedback spam.',
    },
    proSubscription: {
      title: 'Pro subscription and payments',
      intro: 'DumpDeck Pro is a paid tier offering batch analysis, session save, custom tuning rules, and future advanced features.',
      '1': 'Pricing is displayed in the upgrade modal before purchase. We may change prices with at least 30 days notice for existing subscribers.',
      '2': 'Subscription plans auto-renew until canceled. You can cancel at any time from the manage subscription link in the footer.',
      '3': 'Payment is processed by Paddle (Merchant of Record). Paddle handles taxation, invoicing, and refund processing on our behalf.',
      '4': 'Pro features are tied to your license. If your subscription is canceled, refunded, or charged back, your Pro access will be revoked within 24 hours.',
      '5': 'Subscriptions auto-renew until cancelled. You can cancel anytime from the customer portal — access remains until the end of the current billing period.',
      '6': 'Personal use: Your license is for your personal use only. Do not share, sell, or transfer your license to others.',
      '7': 'License is non-transferable: Each license is bound to a single email address.',
    },
    license: {
      title: 'License to use the Service',
      body: 'We grant you a personal, non-exclusive, non-transferable, revocable license to use DumpDeck for the duration of your subscription. The DumpDeck brand, source code, and design are owned by ZH Studio and protected by applicable intellectual property laws.',
    },
    disclaimer: {
      title: 'Disclaimer of warranties',
      body1: 'THE SERVICE IS PROVIDED "AS IS" AND "AS AVAILABLE" WITHOUT WARRANTIES OF ANY KIND, WHETHER EXPRESS OR IMPLIED. WE DO NOT WARRANT THAT THE SERVICE WILL BE ERROR-FREE, UNINTERRUPTED, OR THAT DIAGNOSTIC RESULTS WILL BE ACCURATE OR COMPLETE.',
      body2: 'JVM tuning recommendations provided by the Service are for informational purposes only and do not constitute professional engineering advice. You assume all risk associated with applying any tuning recommendation to your production systems. Always test changes in a non-production environment first.',
    },
    liability: {
      title: 'Limitation of liability',
      body: 'TO THE MAXIMUM EXTENT PERMITTED BY LAW, ZH STUDIO SHALL NOT BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, OR CONSEQUENTIAL DAMAGES ARISING FROM YOUR USE OF THE SERVICE. OUR TOTAL LIABILITY FOR ANY CLAIM ARISING FROM THESE TERMS SHALL NOT EXCEED THE AMOUNT YOU PAID US IN THE 12 MONTHS PRECEDING THE CLAIM.',
    },
    ip: {
      title: 'Intellectual property',
      body: 'All intellectual property rights in DumpDeck — including the DumpDeck name, logo, design, source code, and documentation — are owned by ZH Studio. You may not use our trademarks, branding, or content without prior written consent.',
    },
    termination: {
      title: 'Termination',
      body: 'We may suspend or terminate your access to the Service if you violate these Terms. You may stop using the Service at any time. Refund eligibility upon termination is governed by our Refund Policy.',
    },
    changes: {
      title: 'Changes to these terms',
      body: 'We may update these Terms from time to time. Material changes will be communicated via email (if you have a Pro license) or posted on this page. Continued use after changes take effect constitutes acceptance of the updated Terms.',
    },
    governingLaw: {
      title: 'Governing law',
      body: 'These Terms are governed by the laws of the jurisdiction where ZH Studio is registered, without regard to conflict-of-laws principles. Any disputes will be resolved in the courts of that jurisdiction.',
    },
    contactTitle: 'Contact',
  },
  privacy: {
    title: 'Privacy Policy',
    effective: 'Effective date: {date}',
    overviewTitle: 'Overview',
    overviewBody: 'DumpDeck is a browser-based JVM diagnostics tool. The product is built around a privacy-first principle: your data stays in your browser by default. We do not run any backend that processes your files. This policy explains what little data we do handle and how it is protected.',
    dataWeCollectTitle: 'Data we collect',
    dataFilesTitle: 'Thread dumps and GC logs you analyze:',
    dataFilesBody: 'NEVER leave your browser. Files you drop or paste are parsed locally. No part of the file content is sent to any server.',
    dataLicenseTitle: 'License information (Pro users only):',
    dataLicenseBody: 'When you activate Pro, we link your email to your Pro status. We store your email and activation timestamp on our server to verify your Pro status on subsequent visits and revoke access immediately if you refund or cancel.',
    dataFeedbackTitle: 'Feedback you submit (optional):',
    dataFeedbackBody: 'When you submit feedback, your text and any images are sent to our server for our team to review. We use this to fix bugs and improve the product.',
    dataPaymentTitle: 'Payment information:',
    dataPaymentBody: 'Payments are processed by Paddle, our Merchant of Record. Paddle collects your name, email, and card details directly — we never see or store your full card number. We receive your email, the plan you purchased, and a transaction ID from Paddle via webhook.',
    dataWhereTitle: 'Where data is stored',
    dataWhereLocal: 'Your files (thread dumps, GC logs) — in your browser memory only. Cleared when you close the tab.',
    dataWhereKV: 'License records — stored on our server.',
    dataWhereR2: 'Feedback screenshots — stored by us.',
    dataWhereGH: 'Feedback issue text — stored as private issues, only our team can read.',
    thirdPartiesTitle: 'Third-party processors',
    tpPaddle: 'Processes payments, sends receipts, manages subscriptions, and handles refunds. Paddle\'s privacy policy applies to payment data. See paddle.com/legal/privacy.',
    tpCloudflare: 'Our hosting provider — hosts our website, API endpoints, and storage. See their privacy policy.',
    tpGitHub: 'Stores user feedback as private issues in a private repo. Only the repo owner can read them. See github.com/site/privacy.',
    tpFooter: 'Each processor is contractually bound to their own privacy commitments. We share only what is strictly necessary for them to perform their function.',
    retentionTitle: 'Data retention',
    retentionLocal: 'Browser data: stays in your browser until you clear it. You have full control — we cannot delete it remotely.',
    retentionKV: 'License: kept while your subscription is active; deleted immediately upon refund or cancellation.',
    retentionR2: 'Feedback images: kept until you ask us to delete them. Email support to remove specific feedback.',
    rightsTitle: 'Your rights',
    rightsBody: 'You have the right to access, delete, or export your data. For browser-local data, clear it via your browser settings. For server-side data, email us.',
    rightsAccess: 'Access: ask us what data we hold about you',
    rightsDelete: 'Delete: request deletion of your license record, feedback images, and feedback issues',
    rightsExport: 'Export: receive a copy of any data we hold about you',
    rightsCta: 'Email support to exercise these rights',
    cookiesTitle: 'Cookies',
    cookiesBody: 'We do not use behavioral tracking cookies or cross-site trackers. We may set first-party cookies for essential functionality (e.g., session state). We never sell or share cookie data with third parties.',
    analyticsTitle: 'Analytics',
    analyticsBody: 'We may use privacy-respecting analytics (e.g., Plausible) to count anonymous visitors and understand which features are used. No cross-site tracking, no behavioral profiling, no selling data. Pro users see no analytics scripts.',
    selfProofTitle: 'Self-proof: how to verify',
    intro: 'Your thread dumps and GC logs never leave your browser. All parsing is local.',
    verify: 'Verify via F12',
    verifySteps: [
      'Open DevTools (F12 or Cmd+Opt+I).',
      'Switch to the Network tab.',
      'Drop a thread dump or paste GC log.',
      'Watch the Network tab — no request should contain your dump content. Only static assets and (optionally) anonymous analytics pings.',
    ],
    swTitle: 'Service Worker (PWA)',
    swDesc: 'The service worker caches only static assets (app bundle, fonts, icons). It does not intercept or read user files.',
    dontTrack: 'We don\'t track you',
    dontTrackDesc: 'We don\'t track your identity across sessions. We don\'t sell your data. Your dump content stays in your browser — that\'s the core promise, and you can verify it via F12.',
    source: 'Source code',
    sourceDesc: "The parser core is a separate package that runs in the background. The full source is in this repo.",
    contactTitle: 'Contact',
  },
  export: {
    markdown: 'Markdown (.md)',
    json: 'JSON (.json)',
    pdf: 'PDF (via print)',
  },
  lang: {
    en: 'EN',
    zh: '中文',
  },
};
